import React from 'react'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import type {MetaTagsProps} from '_types'

/**
 * @name MetaTags
 *
 * @component
 *
 * @description
 * Sets the page's metadata including title, description, keywords metadata, JSON-LD script and robots.
 *
 * @param {MetaTagsProps} props - The props for the MetaTags component.
 * @param {MessageDescriptor | string} props.title - The title metadata for the page.
 * @param {MessageDescriptor | string} props.description - The description metadata for the page.
 * @param {string} [props.keywords] - The keywords metadata for the page.
 * @param {string} [props.jsonLd] - The JSON-LD script for the page.
 * @param {boolean} [props.enableNoIndex=false] - Indicates if to include the noindex nofollow robots meta tag.
 * @param {Array<AlternateUrl>} [props.alternateUrls] - The alternate URLs metadata for the page.
 * @param {string} [props.canonicalUrl] - The canonical URL metadata for the page.
 *
 * @returns {JSX.Element} - The rendered MetaTags component.
 */
const MetaTags = ({title, description, keywords, jsonLd, enableNoIndex = false, alternateUrls, canonicalUrl}: MetaTagsProps): JSX.Element => {
    const {formatMessage} = useIntl()

    const formattedTitle = typeof title === 'string' ? title : formatMessage(title)
    const formattedDescription = typeof description === 'string' ? description : formatMessage(description)
    const formattedKeywords = keywords || formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'})

    return (
        <Helmet>
            <title>{formattedTitle}</title>

            <meta name="description" content={formattedDescription} />

            <meta name="keywords" content={keywords || formattedKeywords} />

            {jsonLd && <script type="application/ld+json">{jsonLd}</script>}

            {enableNoIndex && <meta name="robots" content="noindex, nofollow" />}

            {!enableNoIndex &&
                alternateUrls &&
                alternateUrls.length > 0 &&
                alternateUrls.map((alternateUrl, index) => (
                    <link key={`${alternateUrl.hreflang}-${index}`} rel="alternate" hrefLang={alternateUrl.hreflang} href={alternateUrl.url} />
                ))}

            {!enableNoIndex && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        </Helmet>
    )
}

export {MetaTags}
